import {
  base_colorContentBorder,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_2xl,
  font_family_weight_medium,
  font_family_weight_regular,
  font_sm,
  fontBold,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

import { flyoutSurface, paddingHorizontal } from '../billPayFlyoutChrome.style'

/*
 * Chrome shared with AddVendorFlyout (surface override, header, title, footer,
 * required asterisk) lives in ../billPayFlyoutChrome.style — imported and
 * re-exported below so both Bill Pay flyouts stay identical structurally rather
 * than by comment. Only this flyout's own blocks are defined here.
 */
export {
  flyoutTitle,
  footer,
  footerButtons,
  header,
  requiredAsterisk,
} from '../billPayFlyoutChrome.style'

export default css`
  ${flyoutSurface('PayBillFlyout')}

  &.PayBillFlyout .BaseFlyout-Container {
    /* The preview shelf is an absolutely-positioned sibling that slides out
       from behind the primary pane. React Modal defaults this content box to
       overflow: auto, which clips that left-side motion to the pane's 640px
       bounds. Scrolling remains owned by the form and preview bodies. */
    /* !important is required because React Modal supplies overflow: auto as
       an inline style on this element. */
    overflow: visible !important;
  }

  &.PayBillFlyout .BaseFlyout-Content {
    width: min(640px, 100vw);

    &.secondaryFlyout {
      width: min(460px, max(0px, calc(100vw - min(640px, 100vw))));

      &:not(.showSecondaryContent) .BaseFlyout-Body {
        display: none;
      }

      &.showSecondaryContent {
        right: min(640px, 100vw);
      }

      .BaseFlyout-CloseBar {
        padding: ${spacing(6)} ${spacing(6)} 0;
        margin-bottom: 0;
      }

      .BaseFlyout-Header {
        gap: ${spacing(1)};
        padding: ${spacing(3)} ${spacing(6)} ${spacing(4)};

        h2 {
          margin: 0;
          ${fontBold};
        }

        p {
          margin: 0;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          font-family: ${font_family_weight_regular};
          ${font_sm};
          color: ${text_colorTextSecondary};
        }
      }

      .BaseFlyout-Body {
        padding: ${spacing(6)};
        overflow: hidden;
      }
    }
  }
`

/* The step body fills the remaining flyout height: scrollable field list + a
   pinned footer that stays put while the fields scroll. */
export const stepBody = css`
  display: flex;
  flex-direction: column;
  flex: auto;
  min-height: 0;

  &:focus {
    outline: none;
  }
`

export const fields = css`
  flex: auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing(6)};
  padding: ${spacing(6)} ${paddingHorizontal};
`

/*
 * Titled sections + hairline dividers, mirroring the prototype's
 * `FlyoutSection` / `FlyoutDivider`. This is the same "white surface, grouping
 * by dividers" direction as the flyout body above: hierarchy comes from a
 * tight title→fields cluster and a rule between sections, not from cards.
 *
 * Declared prototype values: title 20px / 24px line-height / medium
 * (= font_2xl + font_family_weight_medium), 20px title→fields, 24px between
 * fields, and a full-width 1px rule in the PRIMARY content-border token.
 */
export const section = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(5)};
`

export const sectionTitle = css`
  margin: 0;
  ${font_2xl}
  font-family: ${font_family_weight_medium};
  /* Explicit 500 so the browser doesn't layer a synthetic bold over
     Rubik-Medium's own weight. */
  font-weight: 500;
  color: ${text_colorTextPrimary};
`

export const sectionFields = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(6)};
`

export const sectionDivider = css`
  height: 1px;
  width: 100%;
  flex-shrink: 0;
  background-color: ${base_colorContentBorder};
`

/* Invoice Date + Due Date sit side by side in a 2-column grid with a 16px gap. */
export const datesRow = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing(4)};

  /*
   * SingleDateInputField renders its placeholder at opacity 0.5, so the two
   * date fields read visibly washed-out next to the Invoice Number, Amount,
   * and Memo placeholders, which are full-strength. Same colour underneath
   * (grey700) — only the opacity differs. Full strength here so all five
   * placeholders in the form match.
   *
   * Scoped rather than fixed in SingleDateInputField for the same reason as
   * the Amount rule above: that component is used across the app, and every
   * date field would change. The date-vs-text placeholder mismatch is
   * app-wide and worth a design decision of its own.
   *
   * !important because the component's own rule is more specific than
   * anything reachable from here.
   */
  .DateInput_input::placeholder {
    opacity: 1 !important;
  }
`
